import { CustomerWithReference, ReferenceModel } from 'generated-types/skills/customer_references';
import { useLocalStorage } from 'hooks/misc/useLocalStorage';
import { useCustomerReferencesHook } from 'hooks/skills/useCustomerReferences';
import { useState } from 'react';
import { Button, ButtonGroup, FlexboxGrid, Panel } from 'rsuite';

import { CustomerReferenceEditForm } from './CustomerReferenceEditForm';

export const CustomerReference = ({
    reference,
    customerWithReference,
    editAllowed,
}: {
    customerWithReference: CustomerWithReference;
    reference: ReferenceModel;
    editAllowed: boolean;
}) => {
    const [theme] = useLocalStorage<'light' | 'dark'>('theme', 'light');
    const [editMode, setEditMode] = useState(false);

    const { refetch: refetchReferences, customersWithoutReferenceRefetch } =
        useCustomerReferencesHook();

    function removeCompanyNameSuffix(name: string) {
        // Remove company name suffix
        return name.toUpperCase().replace(/\s(OYJ|OY|LTD|RY|AB)\b/g, '');
    }

    if (editMode) {
        return (
            <CustomerReferenceEditForm
                reference={reference}
                customer={customerWithReference}
                setEditMode={setEditMode}
                refetchReferences={refetchReferences}
                customersWithoutReferenceRefetch={customersWithoutReferenceRefetch}
            />
        );
    }
    return (
        <Panel
            bordered
            shaded
            key={reference.reference_uuid}
            style={{
                padding: 20,
                marginBottom: 20,
                background: 'white',
                color: theme === 'dark' ? '#0f131a' : 'inherit',
            }}
        >
            <FlexboxGrid justify="space-between">
                <FlexboxGrid.Item>
                    <h4>{customerWithReference.customer.name}</h4>
                </FlexboxGrid.Item>
                {editAllowed && (
                    <FlexboxGrid.Item>
                        <ButtonGroup>
                            <Button onClick={() => setEditMode(true)}>Edit</Button>
                        </ButtonGroup>
                    </FlexboxGrid.Item>
                )}
            </FlexboxGrid>

            <span
                style={{
                    fontSize: 15,
                    margin: 20,
                    textAlign: 'justify',
                    whiteSpace: 'pre-wrap',
                }}
            >
                Permission to use as reference:
                <span style={{ color: reference.permission_to_use ? 'green' : 'red' }}>
                    {reference.permission_to_use ? ' Yes' : ' No'}
                </span>
            </span>

            <FlexboxGrid
                justify="space-around"
                style={{
                    border: '1px solid #ccc',
                    boxShadow: '0 2px 20px rgba(0,0,0,0.15)',
                    marginTop: 20,
                    paddingBottom: 10,
                }}
            >
                {reference.presigned_url && (
                    <FlexboxGrid.Item colspan={24}>
                        <div
                            style={{ position: 'relative', display: 'inline-block', width: '100%' }}
                        >
                            <p style={{ marginBottom: 10 }}>
                                <img
                                    style={{
                                        width: '100%',
                                        maxHeight: 500,
                                        display: 'block',
                                        objectFit: 'cover',
                                    }}
                                    src={reference.presigned_url}
                                />
                            </p>
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 95,
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    paddingLeft: 180,
                                    fontSize: 50,
                                    fontWeight: 'bold',
                                }}
                            >
                                {reference.reference_title !== ''
                                    ? reference.reference_title
                                    : removeCompanyNameSuffix(customerWithReference.customer.name)}
                            </div>
                            {reference.logo_presigned_url && (
                                <img
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 50,
                                        width: 95,
                                        height: 95,
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                    }}
                                    src={reference.logo_presigned_url}
                                />
                            )}
                        </div>
                    </FlexboxGrid.Item>
                )}
                <FlexboxGrid.Item colspan={7}>
                    <strong style={{ fontSize: 15 }}>Challenge</strong>
                    <p
                        style={{
                            fontSize: 12,
                            marginTop: 10,
                            textAlign: 'justify',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {reference.challenge}
                    </p>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={7}>
                    <strong style={{ fontSize: 15 }}>Solution</strong>
                    <p
                        style={{
                            fontSize: 12,
                            marginTop: 10,
                            textAlign: 'justify',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {reference.solution}
                    </p>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={7}>
                    <p
                        style={{
                            fontSize: 12,
                            marginTop: 10,
                            fontStyle: 'italic',
                            textAlign: 'justify',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {reference.quote}
                    </p>
                    {reference.quote_by && (
                        <p
                            style={{
                                paddingLeft: 30,
                                fontSize: 13,
                                textAlign: 'justify',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            <strong>- {reference.quote_by}</strong>
                            <br />
                            {reference.quote_by_title ? `${reference.quote_by_title}` : ''}
                        </p>
                    )}
                </FlexboxGrid.Item>
            </FlexboxGrid>
            <div
                style={{
                    background: '#EDF2FA',
                    padding: 10,
                    marginTop: 10,
                }}
            >
                <strong style={{ fontSize: 15 }}>Speaker Notes</strong>
                <p
                    style={{
                        fontSize: 12,
                        marginTop: 10,
                        textAlign: 'justify',
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {reference.notes}
                </p>
            </div>
        </Panel>
    );
};
